// src/pages/agenda/AgendaPage.js — a Agenda da Cetto (out/26, fase 1).
//
// Agenda nativa no lugar da conexão com o Google Calendar. Fiel ao
// AgendaTela.dc.html (Claude Design), com o que a fase 1 decidiu:
//   - A barra do Copiloto no topo (fase 3, out/26): só supervisor/admin,
//     com a chave de IA da empresa. Ele lê a agenda e PROPÕE marcar,
//     remarcar e cancelar; a proposta aparece tracejada só pra quem pediu
//     e só é gravada no clique em Confirmar (no card dele ou nos Detalhes).
//     "Novo compromisso" continua abrindo o painel lateral — o caminho que
//     existe mesmo sem chave de IA.
//   - Supervisor vê a equipe toda (filtro Equipe) e os disparos de
//     template; agente vê o que é dele e o que atribuíram a ele.
//   - O nome da consulta vem da empresa (Consulta, Reunião, Atendimento…).
//   - Data e hora chegam prontas no fuso da operação; o navegador só faz
//     conta de calendário (ver datas.js).
//   - No celular: Semana e Dia viram lista, Mês vira pontinhos, detalhes e
//     filtros abrem por baixo.
//
// Tema claro/escuro com os tokens --ch-* do ChatPage — e a MESMA escolha:
// quem deixou o chat escuro abre a agenda escura.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { FiChevronLeft, FiChevronRight, FiFilter, FiPlus } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppSidebar from '../../components/AppSidebar';
import BloqueioTeste from '../../components/assinatura/BloqueioTeste';
import Copiloto from '../../components/copiloto/Copiloto';
import TopBar from '../../components/TopBar';
import api from '../../services/Api';
import '../ChatPage.css';
import './Agenda.css';
import {
  MESES, MES_C, add, addMes, agoraNoFuso, cap, diaCurto, diaLongo, dnum, gradeDoMes, hh, inicioSemana, mon, yr,
} from './datas';
import { FONT_TXT, botaoBase, cartao, rotuloSecao } from './estilo';
import ConfigLembrete from './ConfigLembrete';
import Detalhes from './Detalhes';
import Filtros from './Filtros';
import GradeHoras, { limitesDaGrade } from './GradeHoras';
import ListaAgenda from './ListaAgenda';
import MiniCalendario from './MiniCalendario';
import PainelCompromisso from './PainelCompromisso';
import VisaoMes from './VisaoMes';

const CHAVE_TEMA = 'cetto:chat-theme';
const RELER_MS = 60000;

const seta = {
  ...botaoBase, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0,
  border: '1px solid var(--ch-bdr)', borderRadius: '7px', bgcolor: 'var(--ch-card)', color: 'var(--ch-text2)',
  '&:hover': { color: 'var(--ch-text)', bgcolor: 'var(--ch-inset)' },
  '@media (pointer: coarse)': { width: 40, height: 40 },
};

function tituloDoPeriodo(view, cursor, curto) {
  // No celular o título encurta ("Quarta, 14 out 2026"): por extenso ele
  // quebrava em três linhas em 320px ao lado de ‹ Hoje ›.
  if (curto && view === 'dia') return `${diaCurto(cursor)} ${yr(cursor)}`;
  if (curto && view === 'semana') {
    const a = inicioSemana(cursor); const b = add(a, 6);
    return `${dnum(a)} ${MES_C[mon(a)]} – ${dnum(b)} ${MES_C[mon(b)]} ${yr(b)}`;
  }
  if (view === 'semana') {
    const a = inicioSemana(cursor); const b = add(a, 6);
    return mon(a) === mon(b)
      ? `${dnum(a)} – ${dnum(b)} de ${MESES[mon(b)]}, ${yr(b)}`
      : `${dnum(a)} ${MES_C[mon(a)]} – ${dnum(b)} ${MES_C[mon(b)]}, ${yr(b)}`;
  }
  if (view === 'dia') return `${diaLongo(cursor)}, ${yr(cursor)}`;
  return `${cap(MESES[mon(cursor)])} ${yr(cursor)}`;
}

export default function AgendaPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const celular = useMediaQuery(theme.breakpoints.down('md'));
  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } }, []);

  // ── Tema (o mesmo do chat) ────────────────────────────────────────────
  const [tema, setTema] = useState(() => {
    try { return localStorage.getItem(CHAVE_TEMA) === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
  });
  useLayoutEffect(() => {
    const el = document.documentElement;
    if (tema === 'dark') el.setAttribute('data-theme', 'dark'); else el.removeAttribute('data-theme');
    return () => el.removeAttribute('data-theme');
  }, [tema]);
  const trocarTema = useCallback(() => setTema((t) => {
    const n = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem(CHAVE_TEMA, n); } catch { /* storage indisponível */ }
    return n;
  }), []);

  // ── Dados ─────────────────────────────────────────────────────────────
  const [config, setConfig] = useState(null);
  const [eventos, setEventos] = useState([]);
  // Fase 3: propostas do Copiloto ainda não confirmadas (só as de quem está
  // logado — o servidor já filtra). Desenhadas tracejadas.
  const [propostas, setPropostas] = useState([]);
  const [carregado, setCarregado] = useState(false);
  const [erroCarga, setErroCarga] = useState('');
  const [agora, setAgora] = useState({ hoje: null, h: 0 });
  const [view, setView] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [selId, setSelId] = useState(null);
  // Fase 3: horário pra onde a grade rola quando o Copiloto propõe ou
  // confirma algo fora da faixa visível (o "olha, deixei aqui" do design).
  const [foco, setFoco] = useState(null);
  const focar = (hhmm) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
    if (m) setFoco({ h: Number(m[1]) + Number(m[2]) / 60, n: Date.now() });
  };
  const [tipos, setTipos] = useState({ consulta: true, disparo: true, lembrete: true, tarefa: true });
  const [pessoas, setPessoas] = useState({});
  const [painel, setPainel] = useState({ aberto: false, modo: 'novo', ev: null, inicial: null });
  const [ocupado, setOcupado] = useState(false);
  const [erroAcao, setErroAcao] = useState('');
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [configLembrete, setConfigLembrete] = useState(false);
  const [busca, setBusca] = useSearchParams();
  const gridRef = useRef(null);
  const copilotoRef = useRef(null);

  const carregarConfig = useCallback(async () => {
    const { data } = await api.get('/agenda/config');
    setConfig(data);
    setAgora(agoraNoFuso(data.fuso));
    return data;
  }, []);

  useEffect(() => {
    carregarConfig()
      .then((c) => { setCursor((x) => x || c.hoje); })
      .catch(() => setErroCarga('Não foi possível abrir a agenda. Tente de novo em instantes.'));
  }, [carregarConfig]);

  useEffect(() => { if (view === null) setView(celular ? 'dia' : 'semana'); }, [celular, view]);

  // Fase 2: o aviso do sino leva pra /agenda?evento=…&data=… — abre no dia
  // do compromisso, com ele selecionado. Vale também com a agenda já aberta
  // (o clique no sino só troca a busca da URL).
  useEffect(() => {
    const evento = busca.get('evento');
    const dia = busca.get('data');
    if (!evento && !dia) return;
    if (dia && /^\d{4}-\d{2}-\d{2}$/.test(dia)) setCursor(dia);
    if (evento) setSelId(evento);
    setBusca({}, { replace: true });
  }, [busca, setBusca]);

  // Período carregado: a grade do mês do mini calendário + a semana na
  // tela (que pode atravessar o mês). Até ~48 dias, uma chamada só.
  const janela = useMemo(() => {
    if (!cursor) return null;
    const g = gradeDoMes(cursor);
    const s0 = inicioSemana(cursor);
    const fimMes = add(g.ini, g.n - 1);
    const s1 = add(s0, 6);
    return { de: g.ini < s0 ? g.ini : s0, ate: fimMes > s1 ? fimMes : s1 };
  }, [cursor]);

  const carregarEventos = useCallback(async () => {
    if (!janela) return;
    try {
      const { data } = await api.get('/agenda/events', { params: janela });
      setEventos(data.eventos || []);
      setPropostas(data.propostas || []);
      setErroCarga('');
    } catch {
      setErroCarga('Não foi possível atualizar a agenda. Os compromissos abaixo podem estar desatualizados.');
    } finally {
      setCarregado(true);
    }
  }, [janela]);

  useEffect(() => { carregarEventos(); }, [carregarEventos]);

  useEffect(() => {
    const id = setInterval(() => {
      if (config) setAgora(agoraNoFuso(config.fuso));
      if (document.visibilityState === 'visible') carregarEventos();
    }, RELER_MS);
    return () => clearInterval(id);
  }, [config, carregarEventos]);

  // ── Derivados ─────────────────────────────────────────────────────────
  const visivel = useCallback((e) => tipos[e.tipo] !== false
    && (!e.responsavel || pessoas[e.responsavel.id] !== false), [tipos, pessoas]);

  const todos = useMemo(() => [...eventos, ...propostas], [eventos, propostas]);

  const porDia = useMemo(() => {
    const m = {};
    todos.filter(visivel).forEach((e) => { (m[e.data] = m[e.data] || []).push(e); });
    return m;
  }, [todos, visivel]);

  const comEvento = useMemo(() => new Set(todos.map((e) => e.data)), [todos]);

  const diasDaVista = useMemo(() => {
    if (!cursor || !view) return [];
    if (view === 'semana') { const a = inicioSemana(cursor); return Array.from({ length: 7 }, (_, i) => add(a, i)); }
    if (view === 'dia') return [cursor];
    return gradeDoMes(cursor).dias.filter((s) => s.slice(0, 7) === cursor.slice(0, 7));
  }, [cursor, view]);

  const contagem = useMemo(() => {
    const c = {};
    const set = new Set(diasDaVista);
    eventos.filter((e) => set.has(e.data)).forEach((e) => { c[e.tipo] = (c[e.tipo] || 0) + 1; });
    return c;
  }, [eventos, diasDaVista]);

  const nPropostas = diasDaVista.reduce((n, s) => n + (porDia[s] || []).filter((e) => e.proposta).length, 0);
  const nVisiveis = diasDaVista.reduce((n, s) => n + (porDia[s]?.length || 0), 0) - nPropostas;
  const subtitulo = `${nVisiveis} compromisso${nVisiveis === 1 ? '' : 's'}${nPropostas ? ` · ${nPropostas} proposta${nPropostas > 1 ? 's' : ''} do Copiloto aguardando você` : ''}`;
  const sel = todos.find((e) => e.id === selId) || null;
  const nome = config?.nome_consulta;

  const limites = useMemo(() => limitesDaGrade(diasDaVista.flatMap((s) => porDia[s] || [])), [diasDaVista, porDia]);

  // ── Navegação ─────────────────────────────────────────────────────────
  const irDia = (s) => { setCursor(s); setView('dia'); };
  const nav = (d) => setCursor((c) => (view === 'semana' ? add(c, 7 * d) : view === 'dia' ? add(c, d) : addMes(c, d)));
  const irHoje = () => { if (config) setCursor(agora.hoje || config.hoje); };
  const selecionar = (id) => { setSelId(id); setErroAcao(''); };

  // ── Ações ─────────────────────────────────────────────────────────────
  const acao = async (fn) => {
    if (ocupado) return;
    setOcupado(true); setErroAcao('');
    try { await fn(); await carregarEventos(); } catch (err) {
      setErroAcao(err?.response?.data?.detail || 'Não foi possível concluir. Tente de novo.');
    } finally { setOcupado(false); }
  };
  const onStatus = (ev, status) => acao(() => api.patch(`/agenda/events/${ev.id}`, { status }));
  const onConcluir = (ev, concluido) => acao(() => api.patch(`/agenda/events/${ev.id}`, { concluido }));
  const onRemover = (ev) => acao(async () => { await api.delete(`/agenda/events/${ev.id}`); setSelId(null); });
  const onCancelarDisparo = (ev) => acao(() => api.delete(`/lembretes/${ev.disparo.id}`));
  const onAbrirConversa = (ev) => { if (ev.contato?.device_id) navigate(`/chat/${ev.contato.device_id}/${ev.contato.id}`); };
  const onAbrirDisparos = () => navigate('/contatos?lembretes=1');
  const onNome = async (chave) => {
    try { const { data } = await api.put('/agenda/config', { nome_consulta: chave }); setConfig(data); await carregarEventos(); } catch (err) {
      setErroAcao(err?.response?.data?.detail || 'Não foi possível mudar o nome.');
    }
  };
  const abrirNovo = (data, ini) => setPainel({
    aberto: true, modo: 'novo', ev: null,
    inicial: { data: data || (view === 'mes' ? cursor : (diasDaVista.includes(agora.hoje) ? agora.hoje : cursor)), ini: ini != null ? hh(ini) : (agora.h < 19 ? hh(Math.ceil(agora.h)) : '09:00') },
  });
  // Fase 3: confirmar ou descartar a proposta pelos Detalhes. Mesma rota do
  // card do Copiloto — e o card de lá é relido pra mostrar o estado novo.
  const onConfirmarProposta = async (ev) => {
    if (ocupado) return;
    setOcupado(true); setErroAcao('');
    try {
      const { data } = await api.post(`/copilot/actions/${ev.proposta.acao_id}/confirm`, { contatos_vistos: 0 });
      if (data.evento) {
        setSelId(data.evento.id);
        if (!diasDaVista.includes(data.evento.data)) setCursor(data.evento.data);
        focar(data.evento.ini);
      } else setSelId(null);
    } catch (err) {
      setErroAcao(err?.response?.data?.detail || 'Não foi possível confirmar. Tente de novo.');
    } finally {
      setOcupado(false);
      await carregarEventos();
      copilotoRef.current?.recarregar();
    }
  };
  const onDescartarProposta = async (ev) => {
    if (ocupado) return;
    setOcupado(true); setErroAcao('');
    try {
      await api.post(`/copilot/actions/${ev.proposta.acao_id}/cancel`);
      setSelId(null);
    } catch (err) {
      setErroAcao(err?.response?.data?.detail || 'Não foi possível descartar. Tente de novo.');
    } finally {
      setOcupado(false);
      await carregarEventos();
      copilotoRef.current?.recarregar();
    }
  };
  // Resposta nova do Copiloto com proposta: a agenda vai até o dia dela e
  // seleciona — é o "olha, deixei aqui, tracejado" do design. No celular só
  // vai até o dia: selecionar abriria os Detalhes por cima do card que a
  // pessoa acabou de receber (e que já tem Confirmar/Descartar).
  const aoResponderCopiloto = async (msg) => {
    const card = (msg?.acoes || []).find((a) => a.tipo === 'compromisso' && a.status === 'pending');
    if (!card) return;
    setCursor(card.evento.data);
    focar(card.evento.ini);
    if (!celular) setSelId(card.operacao !== 'cancelar' ? `proposta:${card.acao_id}` : (card.evento?.id || null));
    await carregarEventos();
  };
  const aoMudarCopiloto = async (card, evento) => {
    await carregarEventos();
    if (card?.status === 'executed' && evento) {
      setCursor(evento.data);
      focar(evento.ini);
      if (!celular) setSelId(evento.id);
    }
  };
  const aoVerNaAgenda = ({ id, data, ini }) => { if (data) setCursor(data); if (ini) focar(ini); if (id) setSelId(id); };

  const onSalvo = (ev) => {
    setSelId(ev.id);
    if (!diasDaVista.includes(ev.data)) setCursor(ev.data);
    carregarEventos();
  };

  // ── Peças ─────────────────────────────────────────────────────────────
  const segmentos = (
    <Box role="tablist" aria-label="Visão" sx={{ display: 'flex', flex: 'none', gap: '2px', p: '3px', bgcolor: 'var(--ch-inset)', border: '1px solid var(--ch-bdr)', borderRadius: '8px' }}>
      {[['Mês', 'mes'], ['Semana', 'semana'], ['Dia', 'dia']].map(([rotulo, v]) => (
        <Box key={v} component="button" type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)}
          sx={{ ...botaoBase, border: 'none', p: '6px 14px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: view === v ? 700 : 500,
            color: view === v ? 'var(--ch-text)' : 'var(--ch-text2)', bgcolor: view === v ? 'var(--ch-card)' : 'transparent',
            boxShadow: view === v ? '0 1px 3px rgba(15,28,50,.10)' : 'none', '@media (pointer: coarse)': { minHeight: 36, px: '12px' },
            // Em 320px o "+" ainda caía pra outra linha: abas mais justas abaixo de 360.
            '@media (max-width: 359.95px)': { px: '8px' } }}>
          {rotulo}
        </Box>
      ))}
    </Box>
  );
  const navegacao = (
    <Box sx={{ display: 'flex', flex: 'none', alignItems: 'center', gap: '6px' }}>
      <Box component="button" type="button" aria-label="Anterior" onClick={() => nav(-1)} sx={seta}><FiChevronLeft size={15} /></Box>
      <Box component="button" type="button" onClick={irHoje}
        sx={{ ...seta, width: 'auto', px: '12px', color: 'var(--ch-text)', fontSize: '0.75rem', fontWeight: 600, '@media (pointer: coarse)': { height: 40, px: '14px' } }}>Hoje</Box>
      <Box component="button" type="button" aria-label="Próximo" onClick={() => nav(1)} sx={seta}><FiChevronRight size={15} /></Box>
    </Box>
  );
  const botaoNovo = (
    <Box component="button" type="button" onClick={() => abrirNovo()} aria-label="Novo compromisso"
      sx={{ ...botaoBase, flex: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', height: 32, px: celular ? '12px' : '14px', border: 'none', borderRadius: '6px',
        bgcolor: 'var(--ch-green)', color: 'var(--ch-on-green)', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.02em', '&:hover': { bgcolor: 'var(--ch-green-hover)' },
        '@media (pointer: coarse)': { height: 40 } }}>
      <FiPlus size={14} />
      {/* Abaixo de 360px fica só o "+" (o nome segue no aria-label): com o
          texto, o botão caía sozinho numa terceira linha em 320px. */}
      <Box component="span" sx={{ '@media (max-width: 359.95px)': { display: 'none' } }}>{celular ? 'Novo' : 'Novo compromisso'}</Box>
    </Box>
  );
  const resumoDia = `${porDia[cursor]?.length || 0 ? `${porDia[cursor].length} compromisso${porDia[cursor].length > 1 ? 's' : ''}` : 'Sem compromissos'}`;
  const filtros = config && (
    <Filtros tipos={tipos} onTipo={(t) => setTipos((x) => ({ ...x, [t]: !x[t] }))} contagem={contagem} nome={nome}
      equipe={config.equipe} pessoas={pessoas} onPessoa={(id) => setPessoas((x) => ({ ...x, [id]: x[id] === false }))}
      veEquipe={config.ve_equipe} opcoesNome={config.opcoes_nome} onNome={onNome}
      lembrete={config.lembrete_cliente} onConfigLembrete={() => { setFiltrosAbertos(false); setConfigLembrete(true); }} />
  );
  const detalhes = (semCartao) => (
    <Detalhes ev={sel} nome={nome} hoje={agora.hoje} ocupado={ocupado} erro={erroAcao} semCartao={semCartao}
      onAbrirConversa={onAbrirConversa} onEditar={(ev) => setPainel({ aberto: true, modo: 'editar', ev, inicial: null })}
      onRemarcar={(ev) => setPainel({ aberto: true, modo: 'remarcar', ev, inicial: null })}
      onStatus={onStatus} onConcluir={onConcluir} onRemover={onRemover}
      onCancelarDisparo={onCancelarDisparo} onAbrirDisparos={onAbrirDisparos}
      onConfirmarProposta={onConfirmarProposta} onDescartarProposta={onDescartarProposta}
      lembreteAtivo={!!config?.lembrete_cliente?.ativo} />
  );

  // Fase 3: o Copiloto, com o tema da Agenda (`cp-tema-agenda`, Agenda.css).
  // Só pra quem vê a equipe — o backend recusa o resto, e a barra nem
  // aparece.
  const copiloto = config?.ve_equipe ? (
    <Box className="cp-tema-agenda" sx={{ flex: 'none', minWidth: 0 }}>
      <Copiloto ref={copilotoRef} variante="agenda" nomeConsulta={nome}
        aoResponder={aoResponderCopiloto} aoMudar={aoMudarCopiloto} aoVer={aoVerNaAgenda} />
    </Box>
  ) : null;

  let corpo = null;
  if (cursor && view) {
    if (view === 'mes') {
      corpo = <VisaoMes cursor={cursor} hoje={agora.hoje} porDia={porDia} selId={selId} onSel={selecionar} onDia={irDia} compacto={celular} />;
    } else if (celular) {
      corpo = <ListaAgenda dias={diasDaVista} porDia={porDia} hoje={agora.hoje} selId={selId} onSel={selecionar} onNovo={(d) => abrirNovo(d)} />;
    } else {
      corpo = (
        <GradeHoras ref={gridRef} view={view} dias={diasDaVista} porDia={porDia} hoje={agora.hoje} agoraH={agora.h}
          selId={selId} onSel={selecionar} onDia={irDia} onVazio={(d, h) => abrirNovo(d, h)} h0={limites.h0} h1={limites.h1} resumoDia={resumoDia}
          rolarPara={foco ? foco.h - 1 : (diasDaVista.includes(agora.hoje) ? agora.h - 1.5 : 8)} chaveRolagem={`${view}|${cursor}|${carregado}|${foco?.n || ''}`} />
      );
    }
  }

  const calendario = (
    <Box data-screen-label="Calendário" sx={{ ...cartao, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
      {celular ? (
        <Box sx={{ p: '10px 12px', borderBottom: '1px solid var(--ch-bdr)', display: 'flex', flexDirection: 'column', gap: '10px', flex: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box component="h1" sx={{ m: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--ch-text)', overflowWrap: 'anywhere' }}>{cursor ? tituloDoPeriodo(view, cursor, true) : 'Agenda'}</Box>
              <Box sx={{ fontSize: '0.7rem', color: 'var(--ch-text2)' }}>{subtitulo}</Box>
            </Box>
            {navegacao}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {segmentos}
            <Box sx={{ flex: 1 }} />
            <Box component="button" type="button" onClick={() => setFiltrosAbertos(true)} aria-label="Filtros"
              sx={{ ...seta, width: 40, height: 40 }}><FiFilter size={15} /></Box>
            {botaoNovo}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '12px 16px', borderBottom: '1px solid var(--ch-bdr)', flex: 'none', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: '1 1 200px' }}>
            <Box component="h1" sx={{ m: 0, fontSize: '1.12rem', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--ch-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {cursor ? tituloDoPeriodo(view, cursor) : 'Agenda'}
            </Box>
            <Box component="span" sx={{ fontSize: '0.7rem', color: 'var(--ch-text2)', overflowWrap: 'anywhere' }}>
              {subtitulo}
            </Box>
          </Box>
          {segmentos}
          {navegacao}
          {botaoNovo}
        </Box>
      )}
      {erroCarga && (
        <Box role="alert" sx={{ p: '8px 16px', fontSize: '0.72rem', color: 'var(--ch-amber-d)', bgcolor: 'var(--ch-amber-bg)', borderBottom: '1px solid var(--ch-amber-b)' }}>{erroCarga}</Box>
      )}
      {corpo}
    </Box>
  );

  return (
    <Box data-screen-label="Agenda" sx={{ display: 'flex', height: '100vh', bgcolor: 'var(--ch-bg)', fontFamily: FONT_TXT, color: 'var(--ch-text)', overflow: 'hidden', WebkitFontSmoothing: 'antialiased' }}>
      <AppSidebar user={user} />
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, ml: { xs: 0, md: 'var(--sidebar-width, 260px)' }, transition: 'margin-left 0.25s ease', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TopBar crumbBase="Visão Geral" crumbCur="Agenda" themeMode={tema} onToggleTheme={trocarTema} />
        <BloqueioTeste pagina="agenda" sx={{ display: 'flex', flexDirection: 'column' }}>
        {celular ? (
          <Box sx={{ flex: 1, minHeight: 0, p: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {copiloto}
            {calendario}
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, p: '18px 24px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {copiloto}
          <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '272px minmax(0, 1fr)', gap: '14px' }}>
            <Box sx={{ minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', scrollbarWidth: 'thin', scrollbarColor: 'var(--ch-bdr2) transparent' }}>
              {cursor && (
                <MiniCalendario cursor={cursor} view={view} hoje={agora.hoje} comEvento={comEvento} onDia={irDia}
                  onMes={(d) => setCursor((c) => addMes(c, d))} />
              )}
              {detalhes(false)}
              {filtros}
            </Box>
            {calendario}
          </Box>
          </Box>
        )}
        </BloqueioTeste>
      </Box>

      {celular && (
        <>
          <Drawer anchor="bottom" open={!!sel} onClose={() => setSelId(null)}
            PaperProps={{ 'data-screen-label': 'Detalhes', sx: { bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', borderTop: '1px solid var(--ch-bdr2)', borderRadius: '14px 14px 0 0', maxHeight: '85vh', p: '8px 16px', pb: 'calc(16px + env(safe-area-inset-bottom, 0px))' } }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'var(--ch-bdr2)', mx: 'auto', mb: '10px' }} />
            <Box component="span" sx={{ ...rotuloSecao, display: 'block', mb: '10px' }}>Detalhes</Box>
            {detalhes(true)}
          </Drawer>
          <Drawer anchor="bottom" open={filtrosAbertos} onClose={() => setFiltrosAbertos(false)}
            PaperProps={{ 'data-screen-label': 'Filtros', sx: { bgcolor: 'var(--ch-bg)', borderRadius: '14px 14px 0 0', maxHeight: '85vh', p: '12px', pb: 'calc(16px + env(safe-area-inset-bottom, 0px))' } }}>
            {filtros}
          </Drawer>
        </>
      )}

      {config?.ve_equipe && (
        <ConfigLembrete aberto={configLembrete} nome={nome} onFechar={() => setConfigLembrete(false)}
          onSalvo={async () => { await carregarConfig(); await carregarEventos(); }} />
      )}

      <PainelCompromisso aberto={painel.aberto} modo={painel.modo} ev={painel.ev} inicial={painel.inicial}
        config={config ? { ...config, hoje: agora.hoje || config.hoje } : null}
        onFechar={() => setPainel((p) => ({ ...p, aberto: false }))} onSalvo={onSalvo}
        onRemover={async (ev) => { await api.delete(`/agenda/events/${ev.id}`); setSelId(null); await carregarEventos(); }} />
    </Box>
  );
}
